import { getAllBuildings } from "../../../Redux/Buildings/BuildingActions";
import { getProfitReport } from "../../../Redux/Reports/ReportsActions";
import Loading from "../../../Components/Common/Loading/Loading";
import { AppDispatch, RootState } from "../../../Redux/store";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import exportToExcel from "../../../Util/exportToExcel";
import { useDispatch, useSelector } from "react-redux";
import toastHelper from "../../../Util/toastHelper";
import React, { useEffect, useState } from "react";
import styles from "./ProfitReport.module.scss";
import { IoCloseSharp } from "react-icons/io5";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";

interface ProfitReportType {
  amenigiesSharePercent: number;
  amenigiesShareValue: number;
  bookingRevenue: number;
  buildingName: string;
  membershipRevenue: number;
  ownerSharePercent: number;
  ownerShareValue: number;
  totalRevenue: number;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

const columnDefs: ColDef<ProfitReportType>[] = [
  {
    headerName: "Building",
    field: "buildingName",
    minWidth: 150,
    flex: 1,
  },
  {
    headerName: "Booking Revenue",
    field: "bookingRevenue",
    minWidth: 150,
    flex: 1,
  },
  {
    headerName: "Membership Revenue",
    field: "membershipRevenue",
    minWidth: 150,
    flex: 1,
  },
  {
    headerName: "Total Revenue",
    field: "totalRevenue",
    minWidth: 150,
    flex: 1,
    valueFormatter: (params) => (params?.value ? `$ ${params.value}` : ""),
  },
  {
    headerName: "Owner Share (%)",
    field: "ownerSharePercent",
    minWidth: 150,
    flex: 1,
  },
  {
    headerName: "Owner Share ($)",
    field: "ownerShareValue",
    minWidth: 150,
    flex: 1,
    valueFormatter: (params) =>
      params?.value && params?.value > 0
        ? `$ ${params?.value?.toFixed(2)}`
        : "$ 0",
  },
  {
    headerName: "Amenities Share (%)",
    field: "amenigiesSharePercent",
    minWidth: 150,
    flex: 1,
  },
  {
    headerName: "Amenities Share ($)",
    field: "amenigiesShareValue",
    minWidth: 150,
    flex: 1,
    valueFormatter: (params) =>
      params?.value && params?.value > 0
        ? `$ ${params?.value?.toFixed(2)}`
        : "$ 0",
  },
];

function ProfitReport() {
  const [rowData, setRowData] = useState<ProfitReportType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [search, setSearch] = useState<string>("");
  const [buildingFilter, setBuildingFilter] = useState<string>("");
  const dispatch = useDispatch<AppDispatch>();
  const [startDate, setStartDate] = useState<string>(
    moment().subtract(1, "month").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const { profitReport, profitReportLoading } = useSelector(
    (state: RootState) => state.reports,
  );
  const { buildings } = useSelector((state: RootState) => state.buildings);
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  const onDatesChange = (value: string, key: string) => {
    if (value) {
      if (key === "startDate") {
        if (moment(value).isAfter(moment(endDate))) {
          toastHelper.error("Start date must be before end date");
        } else {
          setStartDate(value);
          dispatch(
            getProfitReport({
              startDate: value,
              endDate,
            }),
          );
        }
      }

      if (key === "endDate") {
        if (moment(value).isBefore(moment(startDate))) {
          toastHelper.error("End date must be after start date");
        } else {
          setEndDate(value);
          dispatch(
            getProfitReport({
              startDate,
              endDate: value,
            }),
          );
        }
      }
    }
  };
  const applyFilters = (rowData: ProfitReportType[]) => {
    let array = [...rowData];

    if (search?.length > 3) {
      array = array.filter((member: ProfitReportType) =>
        member?.buildingName?.toLowerCase()?.includes(search?.toLowerCase()),
      );
    }

    if (buildingFilter) {
      array = array.filter(
        (member: ProfitReportType) => member?.buildingName === buildingFilter,
      );
    }

    return array;
  };

  const filteredRowData = applyFilters(rowData);

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getProfitReport({ startDate, endDate }));
    dispatch(getAllBuildings());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profitReport) {
      setRowData(profitReport);
    }
  }, [profitReport]);

  if (profitReportLoading) {
    return <Loading />;
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.topContainer}>
        <div className={styles.headingContainer}>
          <h1 className={styles.heading}>Profit Report</h1>
        </div>
        <div className={styles.buttonContainer}>
          {filteredRowData?.length > 0 && (
            <button
              className={styles.button}
              onClick={() =>
                exportToExcel({
                  data: filteredRowData || [],
                  fileName: "Profit Report",
                })
              }
            >
              Export
            </button>
          )}
        </div>
      </div>
      <div className={styles.bodyContainer}>
        <div className={styles.doubleInputContainer}>
          <div className={styles.searchContainer}>
            <div className={styles.searchContainerInner}>
              <input
                type="text"
                className={styles.input}
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e?.target?.value || "")}
              />
              <div
                className={styles.iconContainer}
                onClick={() => setSearch("")}
              >
                <IoCloseSharp className={styles.icon} />
              </div>
            </div>
          </div>
          <div className={styles.filtersContainer}>
            <div className={styles.filterItem}>
              <p className={styles.label}>Building</p>
              <select
                className={styles.input}
                value={buildingFilter}
                onChange={(e) => setBuildingFilter(e?.target?.value || "")}
              >
                <option value="">All</option>
                {buildings?.map((building: any, i: number) => {
                  return (
                    <option key={i} value={building?.buildingName}>
                      {building?.buildingName}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className={styles.filterItem}>
              <p className={styles.label}>Start Date</p>
              <input
                type="date"
                className={styles.input}
                value={startDate}
                onChange={(e) =>
                  onDatesChange(e?.target?.value || "", "startDate")
                }
              />
            </div>
            <div className={styles.filterItem}>
              <p className={styles.label}>End Date</p>
              <input
                type="date"
                className={styles.input}
                value={endDate}
                onChange={(e) =>
                  onDatesChange(e?.target?.value || "", "endDate")
                }
              />
            </div>
          </div>
        </div>
        <div className={styles.gridContainer}>
          <AgGridReact
            className={styles.grid}
            rowData={filteredRowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[5, 10, 20, 50, 100]}
            animateRows={true}
            domLayout="autoHeight"
          />
        </div>
      </div>
    </div>
  );
}

export default ProfitReport;
